import { subIdFor } from "./internal.js";
import { isSeriesRef, isSummaryRef } from "./watchRef.js";
const inWindow = (date, dates) => dates === undefined || (date >= dates.from && date <= dates.to);
const valuesFor = (ref, acc) => {
    if (isSummaryRef(ref))
        return { ...acc };
    if (isSeriesRef(ref)) {
        const values = {};
        for (const [date, v] of Object.entries(acc))
            if (inWindow(date, ref.dates))
                values[date] = v;
        return values;
    }
    if (ref.offsets === undefined)
        return { ...acc };
    const values = {};
    for (const offset of ref.offsets) {
        const key = String(offset);
        const v = acc[key];
        if (v !== undefined)
            values[key] = v;
    }
    return values;
};
const touched = (ref, movedKeys) => {
    if (isSummaryRef(ref))
        return true;
    if (isSeriesRef(ref))
        return [...movedKeys].some((date) => inWindow(date, ref.dates));
    return ref.offsets === undefined || ref.offsets.some((offset) => movedKeys.has(String(offset)));
};
const refKey = (ref) => {
    if (isSummaryRef(ref))
        return subIdFor(ref);
    if (isSeriesRef(ref)) {
        const datesKey = ref.dates ? `${ref.dates.from}..${ref.dates.to}` : "*";
        return `series:${ref.id}|${ref.field}|${datesKey}`;
    }
    const offsetsKey = ref.offsets ? [...ref.offsets].map(String).sort().join(",") : "*";
    return `${ref.id}|${ref.field}|${offsetsKey}`;
};
/**
 * @internal The set of live {@link Watcher}s and the update-dispatch fan-out. Created
 * and owned by `createArtis`; consumers never construct this directly.
 */
export class WatcherRegistry {
    options;
    entries = new Map();
    counter = 0;
    lastValues = new Map();
    pruneScheduled = false;
    constructor(options = {}) {
        this.options = options;
    }
    create(onUpdates) {
        this.counter += 1;
        const id = `w${this.counter}`;
        const entry = { refs: new Set(), onUpdates, disposed: false };
        this.entries.set(id, entry);
        const aliveOrThrow = () => {
            if (entry.disposed)
                throw new Error("Watcher: already disposed");
        };
        return {
            add: (ref) => {
                aliveOrThrow();
                if (entry.refs.has(ref)) {
                    throw new Error("Watcher.add: ref already added. Each call must pass a new object.");
                }
                entry.refs.add(ref);
                this.scheduleReplay(entry, ref);
                this.options.onSubsChange?.();
            },
            remove: (ref) => {
                aliveOrThrow();
                if (!entry.refs.has(ref)) {
                    throw new Error("Watcher.remove: ref not found. Did you pass a different object than add() received? " +
                        "Hold the original ref or look it up via watcher.list().");
                }
                entry.refs.delete(ref);
                this.schedulePrune();
                this.options.onSubsChange?.();
            },
            replace: (refs) => {
                aliveOrThrow();
                const currentByKey = new Map();
                for (const r of entry.refs)
                    currentByKey.set(refKey(r), r);
                const nextByKey = new Map();
                for (const r of refs) {
                    const k = refKey(r);
                    if (!nextByKey.has(k))
                        nextByKey.set(k, r);
                }
                let changed = false;
                for (const [k, existing] of currentByKey) {
                    if (!nextByKey.has(k)) {
                        entry.refs.delete(existing);
                        changed = true;
                    }
                }
                for (const [k, incoming] of nextByKey) {
                    if (!currentByKey.has(k)) {
                        entry.refs.add(incoming);
                        this.scheduleReplay(entry, incoming);
                        changed = true;
                    }
                }
                if (changed) {
                    this.schedulePrune();
                    this.options.onSubsChange?.();
                }
            },
            list: () => {
                aliveOrThrow();
                return [...entry.refs];
            },
            dispose: () => {
                if (entry.disposed)
                    throw new Error("Watcher.dispose: already disposed");
                entry.disposed = true;
                entry.refs.clear();
                this.entries.delete(id);
                this.schedulePrune();
                this.options.onSubsChange?.();
            },
        };
    }
    *allRefs() {
        for (const entry of this.entries.values()) {
            if (entry.disposed)
                continue;
            for (const ref of entry.refs)
                yield ref;
        }
    }
    dispatchUpdates(updates) {
        // The worker sends only moved offsets; accumulate them per sub so watchers still get complete values while the wire stays small.
        const moved = new Map();
        for (const u of updates) {
            const acc = this.lastValues.get(u.subId) ?? {};
            for (const [key, value] of Object.entries(u.values))
                acc[key] = value;
            this.lastValues.set(u.subId, acc);
            moved.set(u.subId, new Set(Object.keys(u.values)));
        }
        for (const entry of this.entries.values()) {
            if (entry.disposed)
                continue;
            const delivered = new Map();
            for (const ref of entry.refs) {
                const subId = subIdFor(ref);
                const movedKeys = moved.get(subId);
                const acc = this.lastValues.get(subId);
                if (!movedKeys || !acc)
                    continue;
                // Fire a narrowed ref only when one of its own offsets moved, but deliver its full current values from the accumulated set.
                if (touched(ref, movedKeys))
                    delivered.set(ref, valuesFor(ref, acc));
            }
            if (delivered.size > 0)
                entry.onUpdates(delivered);
        }
    }
    /** Catch a newly added ref up from the accumulated values, so a late subscriber
     * to an already-watched curve is not left waiting for the next change. Delivery is
     * deferred one microtask so `add()` returns before the consumer's callback runs. */
    scheduleReplay(entry, ref) {
        const subId = subIdFor(ref);
        if (!this.lastValues.has(subId))
            return;
        queueMicrotask(() => {
            if (entry.disposed || !entry.refs.has(ref))
                return;
            const acc = this.lastValues.get(subId);
            if (!acc)
                return;
            const values = valuesFor(ref, acc);
            if (Object.keys(values).length > 0)
                entry.onUpdates(new Map([[ref, values]]));
        });
    }
    /** Pruning is deferred to a microtask so it observes the same coalesced ref set the
     * worker is told about: a same-tick remove-and-re-add of a sub keeps its values, exactly
     * as the worker keeps its own last-sent state for that sub and will not resend it. */
    schedulePrune() {
        if (this.pruneScheduled)
            return;
        this.pruneScheduled = true;
        queueMicrotask(() => {
            this.pruneScheduled = false;
            this.pruneValues();
        });
    }
    pruneValues() {
        const live = new Set();
        for (const ref of this.allRefs())
            live.add(subIdFor(ref));
        for (const subId of this.lastValues.keys()) {
            if (!live.has(subId))
                this.lastValues.delete(subId);
        }
    }
}
//# sourceMappingURL=registry.js.map